import { ChevronUpIcon, DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
    AlertDialogHeader, AlertDialogOverlay, Badge, Box, Button, Center,
    Select as ChakraSelect,
    Divider, HStack, Input, SimpleGrid, Table, Tbody, Td, Text, Th, Thead,
    Tr, useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    deleteTestOrder,
    fetchTestOrders,
    restoreTestOrder,
    sendPatientLink
} from '../../../api';
import AccessDenied from '../../../components/AccessDenied';
import Loading from '../../../components/Loading/Loading';
import Pagination from '../../../components/Pagination';
import PopoverId from '../../../components/PopoverId';
import TitleHeader from '../../../components/TitleHeader';
import { hasPermission } from '../../../Utils';

const TestOrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [physicians, setPhysicians] = useState([]);
    const [facilitySelect, setFacilitySelect] = useState(null);
    const [physicianSelect, setPhysicianSelect] = useState(null);

    const [searchParams, setSearchParams] = useState({
        patientName: '',
        status: '',
        startDate: '',
        endDate: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [linkTarget, setLinkTarget] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [tempEmail, setTempEmail] = useState('');
    const [tempPhone, setTempPhone] = useState('');
    const [sendingLink, setSendingLink] = useState(false);

    const toast = useToast();
    const cancelRef = useRef();
    const navigate = useNavigate();
    const itemsPerPage = 25;

    const fetchData = async (page = 1, params = {}) => {
        setLoading(true);
        const query = new URLSearchParams({ page, limit: itemsPerPage, ...params }).toString();
        const result = await fetchTestOrders(query);

        if (result.success) {
            setOrders(result.data.orders);
            setCurrentPage(result.data.currentPage);
            setTotalPages(result.data.totalPages);
        } else {
            setError(result.error || 'Failed to load test orders');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDelete = async (id) => {
        const res = await deleteTestOrder(id);
        if (res.success) {
            toast({
                title: 'Deleted',
                description: 'Test Order deleted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            setOrders(prev => prev.filter(o => o._id !== id));
        } else {
            toast({ title: 'Error', description: res.message, status: 'error', duration: 3000, isClosable: true });
        }
        setDeleteTarget(null);
    };

    const handleRestore = async (id) => {
        const res = await restoreTestOrder(id);
        if (res.success) {
            toast({ title: 'Restored', description: 'Test Order restored', status: 'success', duration: 3000, isClosable: true });
            fetchData(currentPage, searchParams);
        } else {
            toast({ title: 'Error', description: res.message, status: 'error', duration: 3000, isClosable: true });
        }
        setRestoreTarget(null);
    };

    const handleSendLink = async () => {
        if (!linkTarget || !selectedMethod) return;
        setSendingLink(true);

        const contactValue = selectedMethod === 'email' ? tempEmail : tempPhone;
        const res = await sendPatientLink(linkTarget._id, selectedMethod, contactValue);

        toast({
            title: res.success ? 'Success' : 'Error',
            description: res.message,
            status: res.success ? 'success' : 'error',
            duration: 3000,
            isClosable: true
        });

        setLinkTarget(null);
        setSelectedMethod(null);
        setSendingLink(false);
    };

    const handleSearch = async () => {
        setSearching(true);
        await fetchData(1, searchParams);
    };

    const handleClearSearch = () => {
        setSearchParams({ patientName: '', status: '', startDate: '', endDate: '' });
        setSearching(false);
        fetchData(1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (page) => {
        fetchData(page, searchParams);
    };

    if (error) return <AccessDenied msg={error} />;

    return (
        <Box maxW="full" mx="auto" p={8}>
            <VStack spacing={6} align="stretch">
                <TitleHeader title="Test Orders" buttonText="New Test Order" onClick={() => navigate('/reqs/test-orders/create')} />

                {/* Search */}
                <Text fontSize="lg">Search Orders</Text>
                <VStack spacing={4} mb={5} width="full">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="full">
                        <Input
                            name="patientName"
                            placeholder="Patient Name"
                            value={searchParams.patientName}
                            onChange={handleInputChange}
                        />
                        <ChakraSelect
                            placeholder="Status"
                            name="status"
                            value={searchParams.status}
                            onChange={(e) => setSearchParams(prev => ({ ...prev, status: e.target.value }))}
                        >
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </ChakraSelect>
                        <Input
                            name="startDate"
                            type="date"
                            value={searchParams.startDate}
                            onChange={handleInputChange}
                        />
                        <Input
                            name="endDate"
                            type="date"
                            value={searchParams.endDate}
                            onChange={handleInputChange}
                        />
                    </SimpleGrid>

                    <HStack spacing={4}>
                        <Button onClick={handleSearch} colorScheme="teal" size="sm" isLoading={loading}>
                            Search
                        </Button>
                        {searching && (
                            <Button onClick={handleClearSearch} colorScheme="red" size="sm">
                                Clear
                            </Button>
                        )}
                    </HStack>
                </VStack>

                <Divider />

                {loading ? (
                    <Center mt="20%"><Loading /></Center>
                ) : (
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>Patient</Th>
                                <Th>Facility</Th>
                                <Th>Physician</Th>
                                <Th>Status</Th>
                                <Th>Created</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {orders.map((order, index) => (
                                <Tr key={order._id} _hover={{ bg: 'gray.100' }}>
                                    <Td><PopoverId place='Orders' index={index + (currentPage - 1) * itemsPerPage} content={order._id} /></Td>
                                    <Td>{`${order.firstName} ${order.lastName}`}</Td>
                                    <Td>{order.facility?.name || 'N/A'}</Td>
                                    <Td>{order.physician?.fullName || 'N/A'}</Td>
                                    <Td>
                                        <Badge colorScheme={
                                            order.status === 'completed' ? 'green' :
                                                order.status === 'cancelled' ? 'red' : 'orange'
                                        }>
                                            {order.status || 'Pending'}
                                        </Badge>
                                    </Td>
                                    <Td>{new Date(order.createdAt).toLocaleDateString()}</Td>

                                    <Td>
                                        <HStack spacing={2}>
                                            <Button size="xs" colorScheme="blue" onClick={() => navigate(`/reqs/test-orders/view/${order._id}`)}>
                                                <ViewIcon mr={1} /> View
                                            </Button>
                                            {hasPermission('editTestOrders') &&
                                                <Button size="xs" colorScheme="green" onClick={() => navigate(`/reqs/test-orders/edit/${order._id}`)}>
                                                    <EditIcon mr={1} /> Edit
                                                </Button>
                                            }
                                            {hasPermission('sendPatientLink') && (
                                                <Button
                                                    size="xs"
                                                    colorScheme="orange"
                                                    onClick={() => setLinkTarget(order)}
                                                >
                                                    Send Link
                                                </Button>
                                            )}
                                            {hasPermission('deleteTestOrders') &&
                                                <Button
                                                    size="xs"
                                                    colorScheme="red"
                                                    onClick={() => setDeleteTarget(order)}
                                                >
                                                    <DeleteIcon mr={1} /> Delete
                                                </Button>
                                            }
                                            {hasPermission('restoreTestOrders') && order.deletedAt && (
                                                <Button
                                                    size="xs"
                                                    colorScheme="purple"
                                                    onClick={() => setRestoreTarget(order)}
                                                >
                                                    <ChevronUpIcon mr={1} /> Restore
                                                </Button>
                                            )}
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}

                <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
            </VStack>

            {/* Delete Modal */}
            <AlertDialog isOpen={!!deleteTarget} leastDestructiveRef={cancelRef} onClose={() => setDeleteTarget(null)}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Test Order</AlertDialogHeader>
                        <AlertDialogBody>Are you sure you want to delete this order?</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button colorScheme="red" onClick={() => handleDelete(deleteTarget._id)} ml={3}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Restore Modal */}
            <AlertDialog isOpen={!!restoreTarget} leastDestructiveRef={cancelRef} onClose={() => setRestoreTarget(null)}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Restore Test Order</AlertDialogHeader>
                        <AlertDialogBody>Do you want to restore this test order?</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setRestoreTarget(null)}>Cancel</Button>
                            <Button colorScheme="purple" onClick={() => handleRestore(restoreTarget._id)} ml={3}>Restore</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Send Link Modal */}
            <AlertDialog isOpen={!!linkTarget} leastDestructiveRef={cancelRef} onClose={() => setLinkTarget(null)}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Send Patient Link</AlertDialogHeader>
                        <AlertDialogBody>
                            <Text mb={2}>
                                Send link to <strong>{`${linkTarget?.firstName} ${linkTarget?.lastName}`}</strong> via:
                            </Text>
                            <Center>
                                <HStack mb={6} mt={6}>
                                    <Button size="sm" variant={selectedMethod === 'email' ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setSelectedMethod('email')}>
                                        Email
                                    </Button>
                                    <Button size="sm" variant={selectedMethod === 'sms' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setSelectedMethod('sms')}>
                                        Phone
                                    </Button>
                                </HStack>
                            </Center>

                            {selectedMethod === 'email' && (
                                <Box>
                                    <Text fontSize="sm" mb={1}>Email:</Text>
                                    <Input value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} />
                                </Box>
                            )}
                            {selectedMethod === 'sms' && (
                                <Box>
                                    <Text fontSize="sm" mb={1}>Phone:</Text>
                                    <Input value={tempPhone} onChange={(e) => setTempPhone(e.target.value)} />
                                </Box>
                            )}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setLinkTarget(null)}>Cancel</Button>
                            <Button colorScheme="orange" ml={3} isDisabled={!selectedMethod} isLoading={sendingLink} onClick={handleSendLink}>
                                Send Link
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default TestOrdersPage;
